import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "../components/LocaleLink";
import { ArrowLeft, BedDouble, Bath, Maximize, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { fetchProperty, formatPrice, optimizeImg, localized, localizedList } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd, buildFaqSchema, buildListingSchema } from "../components/JsonLd";
import { ContactForm } from "../components/ContactForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    fetchProperty(id)
      .then((d) => { setProperty(d); setActiveImg(0); })
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="pt-44 pb-32 text-center font-body text-foreground/60" data-testid="detail-loading">{t("detail.loading")}</div>;
  }
  if (!property) {
    return (
      <div className="pt-44 pb-32 text-center" data-testid="detail-notfound">
        <Seo title="Property not found | Velmira Living" description="This property is no longer available." noindex />
        <p className="font-headings text-3xl text-foreground">{t("detail.notFound")}</p>
        <Link to="/properties" className="mt-6 inline-block text-primary uppercase tracking-widest text-xs">{t("detail.back")}</Link>
      </div>
    );
  }

  const isRental = property.listing_type === "rental";
  const isMid = property.listing_type === "mid_term";
  const stayRange = isMid
    ? t("detail.stayRange").replace("{min}", property.min_stay_months || 1).replace("{max}", property.max_stay_months || 11)
    : null;
  const title = localized(property.title, lang);
  const description = localized(property.description, lang);
  const features = localizedList(property.features, lang);
  const seoProp = { ...property, title, description };

  return (
    <div data-testid="property-detail-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo title={`${title} | Velmira Living`} description={description} />
      <JsonLd id="listing" data={buildListingSchema(seoProp, window.location.href)} />
      <JsonLd id="detail-faq" data={buildFaqSchema(t("listings.faq"))} />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
        <Link to="/properties" data-testid="detail-back" className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary transition-colors">
          <ArrowLeft size={15} /> {t("detail.back")}
        </Link>

        {/* Gallery */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-9 aspect-[16/10] overflow-hidden bg-secondary">
            <img src={optimizeImg(property.images[activeImg], 1400)} alt={title} decoding="async" className="w-full h-full object-cover" data-testid="detail-main-image" />
          </div>
          <div className="lg:col-span-3 flex lg:flex-col gap-4">
            {property.images.map((img, i) => (
              <button
                key={i}
                data-testid={`detail-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`flex-1 aspect-square lg:aspect-auto overflow-hidden border transition-all duration-300 ${activeImg === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"}`}
              >
                <img src={optimizeImg(img, 400)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-3 py-1.5">
              {isMid ? t("detail.forRentMidTerm") : isRental ? t("detail.forRent") : t("detail.forSale")}
            </span>
            <p className="mt-6 font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground">{property.location}</p>
            <h1 className="mt-3 font-headings text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground">{title}</h1>
            <p className="mt-6 font-body text-3xl text-primary" data-testid="detail-price">
              {formatPrice(property.price, property.price_unit)}
              {isRental && <span className="text-base text-muted-foreground ml-1">/ {t("detail.perNight")}</span>}
              {isMid && <span className="text-base text-muted-foreground ml-1">/ {t("detail.perMonth")}</span>}
            </p>
            {stayRange && (
              <p data-testid="detail-stay-range" className="mt-3 font-accent text-xs uppercase tracking-[0.2em] text-foreground/60">{stayRange}</p>
            )}

            <div className="mt-10 grid grid-cols-3 gap-4 border-y border-border/60 py-8">
              <Stat icon={BedDouble} value={property.bedrooms} label={t("detail.bedrooms")} />
              <Stat icon={Bath} value={property.bathrooms} label={t("detail.bathrooms")} />
              <Stat icon={Maximize} value={`${property.area_sqm} m²`} label={t("detail.area")} />
            </div>

            <div className="mt-10">
              <h2 className="font-headings text-2xl text-foreground">{t("detail.overview")}</h2>
              <p className="mt-4 font-body font-light text-foreground/75 leading-relaxed text-lg">{description}</p>
            </div>

            <div className="mt-10">
              <h2 className="font-headings text-2xl text-foreground">{t("detail.features")}</h2>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 font-body font-light text-foreground/75">
                    <Check size={16} className="text-primary shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 border-t border-border/60 pt-8">
              <h2 className="font-headings text-2xl text-foreground">{isMid ? t("listings.detailMidTermTitle") : isRental ? t("listings.detailRentTitle") : t("listings.detailSaleTitle")}</h2>
              <p className="mt-4 font-body font-light text-foreground/75 leading-relaxed text-lg">{isMid ? t("listings.detailMidTermText") : isRental ? t("listings.detailRentText") : t("listings.detailSaleText")}</p>
            </div>

            <div className="mt-10">
              <h2 className="font-headings text-2xl text-foreground mb-4">{t("listings.faqTitle")}</h2>
              <Accordion type="single" collapsible className="w-full" data-testid="detail-listings-faq">
                {t("listings.faq").map((f, i) => (
                  <AccordionItem key={i} value={`dfaq-${i}`} className="border-border/60">
                    <AccordionTrigger data-testid={`detail-faq-trigger-${i}`} className="font-headings text-lg md:text-xl text-foreground hover:text-primary hover:no-underline text-left py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body font-light text-foreground/70 leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Inquiry sidebar */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 bg-secondary border border-border/60 p-8 md:p-10">
              <h2 className="font-headings text-2xl text-foreground">{t("detail.inquire")}</h2>
              <div className="mt-8">
                <ContactForm propertyId={property.id} defaultInterest={isMid ? "rent_mid" : isRental ? "rent" : "buy"} compact />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label }) => (
  <div className="text-center">
    <Icon size={22} className="text-primary mx-auto" strokeWidth={1.4} />
    <p className="mt-3 font-headings text-2xl text-foreground">{value}</p>
    <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</p>
  </div>
);
